import { useState, useMemo } from "react";
import Icon from "../components/Icon";
import { BtnPrimary, SortTh } from "../components/Atoms";
import { useSortable } from "../hooks/useSortable";
import * as api from "../api";
import Campo from "../components/forms/Campo";
import { clicable } from "../components/ClicableTeclado.jsx";
import { nombreRol, esSuper } from "../utils";

const PAGE_SIZE = 10;

// `puedeEditar` separa consultar de administrar: un gestor ve la plantilla y
// sus equipos, pero crear, editar, cambiar de rol y desactivar es del admin
// (el backend rechaza el resto con 403, esto solo evita el botón inútil).
// `puedeNombrarSuper`: solo un super crea, cambia o quita a un super (el servidor responde 403 SOLO_SUPER al resto, y 409
// ULTIMO_SUPER si se intenta dejar a la empresa sin ninguno).
// `puedeEditarA(u)`: si se puede modificar a ESA persona (lote S6: el admin, solo a su gente; espejo de puedeGestionarUsuario).
// Lo que hace cada nivel de acceso HOY
const DESCRIPCION_ROL = {
  member: "Su Cuaderno, sus proyectos asignados y las tareas de su equipo.",
  gestor: "Gestiona proyectos y tareas, y consulta la plantilla, los equipos y la jornada de su equipo. No crea ni edita usuarios.",
  admin: "Ve a toda la plantilla; gestiona a las personas de sus equipos, su cuaderno, su jornada y sus ausencias.",
  super: "Acceso a todos los equipos, como administrador; además es quien puede nombrar a otros super usuarios.",
};

// `rolesConcedibles`: los roles que quien edita puede conceder (lote S6: solo inferiores al suyo; el servidor responde 403
// ROL_SUPERIOR al resto). El rol actual de la persona se sigue mostrando aunque no se pueda conceder.
export default function UsersPage({ users, setUsers, teams, puedeEditar = false, puedeEditarA = () => true, puedeNombrarSuper = false,
                                    rolesConcedibles = ["member", "gestor", "admin"], showToast }) {
  const editable = u => puedeEditar && !!u && puedeEditarA(u);
  const [editId, setEditId] = useState("hidden");
  const [form,   setForm  ] = useState({ name:"", email:"", role:"member", pwd:"" });
  const [msg,    setMsg   ] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [page,   setPage  ] = useState(1);

  const roleLabel = nombreRol;
  const roleColor = r => r==="super"||r==="admin"?["#a16207","#fef9c3"]:r==="gestor"?["#15803d","#f0fdf4"]:["var(--accent)","var(--accent-bg)"];
  const roleOrder = r => r==="super"?-1:r==="admin"?0:r==="gestor"?1:2;

  const openEdit = u => { if (!editable(u)) return; setEditId(u.id); setForm({ name:u.name, email:u.email, role:u.role, pwd:"" }); setMsg(null); };
  const openNew  = () => { if (!puedeEditar) return; setEditId(null); setForm({ name:"", email:"", role:"member", pwd:"" }); setMsg(null); };
  const cancel   = () => { setEditId("hidden"); setMsg(null); };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { setMsg({ ok:false, txt:"Nombre y email son obligatorios" }); return; }
    if (editId === null && !form.pwd) { setMsg({ ok:false, txt:"La contraseña inicial es obligatoria" }); return; }
    setSaving(true);
    try {
      if (editId === null) {
        const newUser = await api.createUser({ name:form.name, email:form.email, role:form.role, password:form.pwd });
        setUsers(p => [...p, { ...newUser, active:true }]);
        showToast("Usuario creado");
      } else {
        const payload = { name:form.name, email:form.email, role:form.role };
        if (form.pwd) payload.password = form.pwd;
        const updated = await api.updateUser(editId, payload);
        setUsers(p => p.map(u => u.id === editId ? { ...u, ...updated } : u));
        showToast("Usuario actualizado");
      }
      cancel();
    } catch (e) {
      setMsg({ ok:false, txt: e.message || "Error al guardar" });
    }
    setSaving(false);
  };

  const toggleActive = async uid => {
    const user = users.find(u => u.id === uid);
    if (!editable(user)) return;
    try {
      const updated = await api.toggleUser(uid);
      setUsers(p => p.map(u => u.id === uid ? { ...u, active: updated.active } : u));
      showToast(updated.active ? "Usuario activado" : "Usuario desactivado");
    } catch (e) {
      showToast("Error al cambiar estado", "error");
    }
  };

  const cycleRole = async uid => {
    const user = users.find(u => u.id === uid);
    if (!editable(user)) return;
    if (esSuper(user)) { showToast("Un super usuario se cambia desde su ficha", "error"); return; }
    // El ciclo de siempre (admin → gestor → member → admin), solo por los roles que se pueden conceder
    const ciclo = ["admin","gestor","member"].filter(r => rolesConcedibles.includes(r));
    const i = ciclo.indexOf(user.role);
    if (i < 0 || ciclo.length < 2) { showToast("No puedes cambiar el rol de esta persona", "error"); return; }
    const next = ciclo[(i + 1) % ciclo.length];
    try {
      const updated = await api.updateUser(uid, { name:user.name, email:user.email, role:next });
      setUsers(p => p.map(u => u.id === uid ? { ...u, ...updated } : u));
      showToast(`Rol cambiado a ${roleLabel(next)}`);
    } catch (e) {
      showToast(e?.message || "Error al cambiar rol", "error");
    }
  };

  // Enrich users with sort helpers
  const enriched = useMemo(() => users.map(u => {
    const uTeams = teams.filter(t => t.members?.includes(u.id)||t.members?.map(String).includes(String(u.id)));
    return {
      ...u,
      _roleOrder: roleOrder(u.role),
      _teamNames: uTeams.map(t=>t.name).join(", "),
      _teamCount: uTeams.length,
      _uTeams:    uTeams,
      _activeSort: u.active ? 0 : 1,
    };
  }), [users, teams]);

  // Filter by search
  const q = search.toLowerCase();
  const filtered = useMemo(() => !q ? enriched : enriched.filter(u =>
    u.name.toLowerCase().includes(q) ||
    u.email.toLowerCase().includes(q) ||
    roleLabel(u.role).toLowerCase().includes(q) ||
    u._teamNames.toLowerCase().includes(q)
  ), [enriched, q]);

  const { sorted, cfg, sort } = useSortable(filtered);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = sorted.slice((safePage-1)*PAGE_SIZE, safePage*PAGE_SIZE);

  const inS = { width:"100%",padding:"9px 11px",borderRadius:8,border:"1px solid var(--border)",fontSize:13,fontFamily:"var(--font)",boxSizing:"border-box",background:"var(--surface2)",color:"var(--text)",marginBottom:10 };
  const lbS = { fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".06em" };
  const card = { background:"var(--surface)",borderRadius:13,border:"1px solid var(--border)",boxShadow:"var(--shadow-sm)" };
  const TH = { padding:"9px 14px",textAlign:"left",fontWeight:700,fontSize:10,color:"var(--text3)",borderBottom:"1px solid var(--border)",textTransform:"uppercase",letterSpacing:".05em" };

  return (
    <div style={{ maxWidth:900, width:"100%", margin:"0 auto" }}>
      {/* Stats */}
      <div className="kpi-grid" style={{ display:"grid", gridTemplateColumns:`repeat(${users.some(u=>u.role==="super")?5:4},1fr)`, gap:12, marginBottom:18 }}>
        {[
          ["people-fill","var(--accent)",users.length,"Total usuarios"],
          ...(users.some(u=>u.role==="super")?[["shield-lock","#a16207",users.filter(u=>u.role==="super").length,"Super usuarios"]]:[]),
          ["shield-lock","#a16207",users.filter(u=>u.role==="admin").length,"Administradores"],
          ["star-fill","#15803d",users.filter(u=>u.role==="gestor").length,"Gestores"],
          ["person","var(--accent)",users.filter(u=>u.role==="member").length,"Usuarios"],
        ].map(([ic,cl,v,lb]) => (
          <div key={lb} style={{ ...card, padding:"13px 16px", borderTop:"3px solid "+cl }}>
            <Icon name={ic} size={15} color={cl} style={{ marginBottom:4, display:"block" }}/>
            <div style={{ fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:".07em",color:"var(--text3)",marginBottom:2 }}>{lb}</div>
            <div style={{ fontSize:22, fontWeight:800, color:cl, lineHeight:1 }}>{v}</div>
          </div>
        ))}
      </div>

      <div className="users-grid" style={{ display:"grid", gridTemplateColumns:"1fr"+(puedeEditar&&editId!=="hidden"?" 380px":""), gap:16, alignItems:"start" }}>
        {/* Tabla */}
        <div style={{ ...card, overflow:"hidden" }}>
          {/* Cabecera con búsqueda */}
          <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--border)", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
            <span style={{ fontWeight:700, fontSize:13, marginRight:"auto" }}>Usuarios del sistema</span>
            <div style={{ display:"flex",alignItems:"center",gap:7,background:"var(--bg)",borderRadius:8,border:"1px solid var(--border)",padding:"5px 10px",flex:"1 1 200px",minWidth:0,maxWidth:320 }}>
              <Icon name="search" size={12} color="var(--text3)"/>
              <input aria-label="Buscar usuario" value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}
                placeholder="Buscar por nombre, email, rol o equipo…"
                style={{ border:"none",background:"transparent",fontSize:12,fontFamily:"var(--font)",color:"var(--text)",outline:"none",width:"100%",minWidth:0 }}/>
              {search&&<button aria-label="Borrar la búsqueda" onClick={()=>{setSearch("");setPage(1);}}
                style={{minWidth:24,minHeight:24,alignItems:"center",justifyContent:"center",display:"inline-flex", border:"none",background:"none",cursor:"pointer",color:"var(--text3)",padding:0,display:"flex",lineHeight:1 }}>
                <Icon name="x" size={12} color="var(--text3)"/>
              </button>}
            </div>
            {puedeEditar && (
              <BtnPrimary onClick={openNew} style={{ padding:"6px 13px", fontSize:12 }}>
                <Icon name="person-plus-fill" size={12} color="#fff"/>Nuevo usuario
              </BtnPrimary>
            )}
          </div>

          {/* Envoltorio con scroll horizontal: 6 columnas no caben por debajo
              de ~640px y sin esto la tabla desbordaba la pagina entera. */}
          <div className="tbl-wrap">
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr style={{ background:"var(--bg)" }}>
              <SortTh sortKey="name"        cfg={cfg} onSort={sort} style={TH}>Usuario</SortTh>
              <SortTh sortKey="email"       cfg={cfg} onSort={sort} style={TH}>Email</SortTh>
              <SortTh sortKey="_roleOrder"  cfg={cfg} onSort={sort} style={TH}>Nivel</SortTh>
              <SortTh sortKey="_teamCount"  cfg={cfg} onSort={sort} style={TH}>Equipos</SortTh>
              <SortTh sortKey="_activeSort" cfg={cfg} onSort={sort} style={TH}>Estado</SortTh>
              {puedeEditar && <th style={TH}>Acciones</th>}
            </tr></thead>
            <tbody>
              {pageRows.length===0&&(
                <tr><td colSpan={puedeEditar?6:5} style={{padding:28,textAlign:"center",color:"var(--text3)",fontSize:12}}>
                  {search?"Sin resultados para «"+search+"»":"Sin usuarios"}
                </td></tr>
              )}
              {pageRows.map((u, i) => {
                const [tc, tb]  = roleColor(u.role);
                const isSelected = editId === u.id;
                return (
                  <tr key={u.id} {...clicable(() => { isSelected ? cancel() : openEdit(u); }, { activo: editable(u), role: null })}
                    style={{ borderBottom:"1px solid var(--border2)", cursor:editable(u)?"pointer":"default",
                      background:isSelected?"var(--accent-bg)":i%2?"var(--surface2)":"var(--surface)", transition:"background .12s" }}
                    onMouseEnter={e => { if(!isSelected) e.currentTarget.style.background="var(--bg)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background=isSelected?"var(--accent-bg)":i%2?"var(--surface2)":"var(--surface)"; }}>
                    <td style={{ padding:"11px 14px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                        <div style={{ width:32,height:32,borderRadius:"50%",background:isSelected?"var(--accent)":"var(--accent-bg)",color:isSelected?"var(--on-accent)":"var(--accent)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,flexShrink:0 }}>
                          {u.name[0]}
                        </div>
                        <div style={{ fontWeight:600, fontSize:12.5 }}>{u.name}</div>
                      </div>
                    </td>
                    <td style={{ padding:"11px 14px", fontSize:12, color:"var(--text3)" }}>{u.email}</td>
                    <td style={{ padding:"11px 14px" }}>
                      <span {...clicable(e => { e.stopPropagation(); cycleRole(u.id); }, { activo: editable(u), label: `Cambiar rol de ${u.name}: ${roleLabel(u.role)}` })}
                        title={editable(u)?"Clic para cambiar rol":""}
                        style={{ fontSize:10,fontWeight:700,padding:"3px 9px",borderRadius:20,cursor:editable(u)?"pointer":"default",userSelect:"none",background:tb,color:tc,border:"1px solid "+tc+"44" }}>
                        {roleLabel(u.role)}
                      </span>
                    </td>
                    <td style={{ padding:"11px 14px" }}>
                      <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                        {u._uTeams.length ? u._uTeams.map(t => (
                          <span key={t.id} style={{ fontSize:10,padding:"2px 6px",borderRadius:20,background:t.color+"15",color:t.color,fontWeight:700,border:"1px solid "+t.color+"30",display:"flex",alignItems:"center",gap:3 }}>
                            <Icon name={t.icon||"people-fill"} size={8} color={t.color}/>{t.name}
                          </span>
                        )) : <span style={{ fontSize:11, color:"var(--text3)" }}>—</span>}
                      </div>
                    </td>
                    <td style={{ padding:"11px 14px" }}>
                      <span style={{ fontSize:10,fontWeight:700,padding:"3px 9px",borderRadius:20,background:u.active?"#f0fdf4":"#f9fafb",color:u.active?"var(--ok-text)":"var(--text3)",border:"1px solid "+(u.active?"var(--ok-line)":"var(--border)") }}>
                        {u.active ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    {puedeEditar && (
                      <td style={{ padding:"11px 14px" }}>
                        {editable(u) && <div style={{ display:"flex", gap:5 }}>
                          <button className="btn" onClick={e => { e.stopPropagation(); isSelected ? cancel() : openEdit(u); }}
                            style={{ padding:"4px 8px",borderRadius:7,border:"1px solid "+(isSelected?"var(--accent)":"var(--border)"),cursor:"pointer",background:isSelected?"var(--accent-bg)":"var(--bg)",color:isSelected?"var(--accent)":"var(--text2)",display:"flex",alignItems:"center",gap:3,fontSize:11,fontFamily:"var(--font)" }}>
                            <Icon name="pencil" size={11} color={isSelected?"var(--accent)":"var(--text2)"}/>Editar
                          </button>
                          <button className="btn" onClick={e => { e.stopPropagation(); toggleActive(u.id); }}
                            style={{ padding:"4px 8px",borderRadius:7,border:"1px solid "+(u.active?"var(--red-line)":"var(--ok-line)"),cursor:"pointer",background:u.active?"#fef2f2":"#f0fdf4",color:u.active?"var(--red)":"#16a34a",display:"flex",alignItems:"center",gap:3,fontSize:11,fontFamily:"var(--font)" }}>
                            <Icon name={u.active?"eye-slash":"eye"} size={11} color={u.active?"var(--red-text)":"var(--ok-text)"}/>{u.active?"Desactivar":"Activar"}
                          </button>
                        </div>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div style={{ padding:"10px 16px", borderTop:"1px solid var(--border)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontSize:11, color:"var(--text3)" }}>
                {(safePage-1)*PAGE_SIZE+1}–{Math.min(safePage*PAGE_SIZE,sorted.length)} de {sorted.length} usuarios
              </span>
              <div style={{ display:"flex", gap:4 }}>
                <button className="btn" onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={safePage===1}
                  style={{ padding:"4px 10px",borderRadius:7,border:"1px solid var(--border)",cursor:safePage===1?"not-allowed":"pointer",background:"var(--surface)",color:"var(--text2)",fontSize:12,fontFamily:"var(--font)",opacity:safePage===1?.4:1 }}>
                  ‹ Anterior
                </button>
                {Array.from({length:totalPages},(_,i)=>i+1).map(n=>(
                  <button key={n} className="btn" onClick={()=>setPage(n)}
                    style={{ padding:"4px 9px",borderRadius:7,border:"1px solid "+(n===safePage?"var(--accent)":"var(--border)"),cursor:"pointer",background:n===safePage?"var(--accent-bg)":"var(--surface)",color:n===safePage?"var(--accent)":"var(--text2)",fontSize:12,fontFamily:"var(--font)",fontWeight:n===safePage?700:400 }}>
                    {n}
                  </button>
                ))}
                <button className="btn" onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={safePage===totalPages}
                  style={{ padding:"4px 10px",borderRadius:7,border:"1px solid var(--border)",cursor:safePage===totalPages?"not-allowed":"pointer",background:"var(--surface)",color:"var(--text2)",fontSize:12,fontFamily:"var(--font)",opacity:safePage===totalPages?.4:1 }}>
                  Siguiente ›
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Panel edición */}
        {puedeEditar && editId !== "hidden" && (
          <div style={{ ...card, padding:20, position:"sticky", top:0 }}>
            <div style={{ fontWeight:700,fontSize:13,marginBottom:14,display:"flex",alignItems:"center",gap:7 }}>
              <Icon name={editId===null?"person-plus-fill":"pencil-square"} size={15} color="var(--accent)"/>
              {editId===null ? "Nuevo usuario" : "Editar: "+users.find(u=>u.id===editId)?.name}
            </div>
            <Campo><label style={lbS}>Nombre completo</label>
            <input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} style={inS}/></Campo>
            <Campo><label style={lbS}>Email</label>
            <input type="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} style={inS}/></Campo>
            <Campo><label style={lbS}>
              {editId===null?"Contraseña inicial":"Nueva contraseña"}
              {editId!==null && <span style={{ fontWeight:400, textTransform:"none" }}> (vacío = no cambiar)</span>}
            </label>
            <input type="password" value={form.pwd} onChange={e=>setForm(p=>({...p,pwd:e.target.value}))} placeholder="••••••••" style={inS}/></Campo>
            <Campo><label style={lbS}>Nivel de acceso</label>
            <div style={{ display:"flex", flexWrap:"wrap", gap:7, marginBottom:6 }}>
              {["member","gestor","admin","super"].filter(v => rolesConcedibles.includes(v) || v===form.role || (v==="super" && puedeNombrarSuper)).map(v => [v,nombreRol(v)]).map(([v,lb]) => (
                <button key={v} className="btn" onClick={()=>setForm(p=>({...p,role:v}))} aria-pressed={form.role===v}
                  style={{ flex:"1 1 40%",minHeight:36,padding:"8px 4px",borderRadius:8,border:"1px solid var(--border)",cursor:"pointer",fontSize:11.5,fontFamily:"var(--font)",fontWeight:form.role===v?700:400,
                    background:form.role===v?"var(--accent-bg)":"var(--surface)",color:form.role===v?"var(--accent)":"var(--text2)" }}>
                  {lb}
                </button>
              ))}
            </div>
            <p style={{ fontSize:11.5, color:"var(--text3)", margin:"0 0 14px", lineHeight:1.5 }}>{DESCRIPCION_ROL[form.role] || DESCRIPCION_ROL.member}</p></Campo>
            {msg && (
              <div style={{ padding:"8px 12px",borderRadius:8,fontSize:12,marginBottom:12,background:msg.ok?"#f0fdf4":"#fef2f2",color:msg.ok?"#15803d":"#dc2626",border:"1px solid "+(msg.ok?"var(--ok-line)":"var(--red-line)") }}>
                {msg.txt}
              </div>
            )}
            <div style={{ display:"flex", gap:8 }}>
              <button className="btn" onClick={cancel}
                style={{ flex:1,padding:"9px",borderRadius:9,border:"1px solid var(--border)",cursor:"pointer",fontSize:12,background:"var(--surface2)",color:"var(--text2)",fontFamily:"var(--font)" }}>
                Cancelar
              </button>
              <BtnPrimary onClick={save} disabled={saving} style={{ flex:2, justifyContent:"center" }}>
                <Icon name={editId===null?"person-plus-fill":"floppy2-fill"} size={13} color="#fff"/>
                {saving ? "Guardando…" : editId===null ? "Crear usuario" : "Guardar cambios"}
              </BtnPrimary>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
